import { monthKeysBack, startOfToday } from './dates';
import type {
  Material,
  MaterialId,
  MonthlyPoint,
  SupplierId,
  SupplierMaterialMetrics,
  TcoBreakdown,
} from './types';

export const MATERIALS: Record<MaterialId, Material> = {
  'MAT-THHN': {
    id: 'MAT-THHN',
    name: 'Copper building wire, 1/0 gauge, 500 ft reels',
    unit: 'C',
    annualVolume: 142_000,
    annualVolumeLabel: '14.2M ft',
    sites: ['DAL', 'PHX'],
    usedIn: [
      'Contractor resale',
      'Cut-to-length service',
      'Control panel shop',
    ],
    categoryId: 'cat-wire',
    description:
      'Stranded 1/0 gauge copper building wire rated 600 V, sold to electrical contractors and used by the cut-to-length service and the control panel shop. Priced per 100 ft. Approved from three suppliers across two continents.',
  },
  'MAT-LED': {
    id: 'MAT-LED',
    name: 'LED high-bay light fixture, 150 W',
    unit: 'fixture',
    annualVolume: 130_000,
    annualVolumeLabel: '130,000 fixtures',
    sites: ['DAL', 'PHX', 'HAY'],
    usedIn: ['Pillar Pro own brand', 'Contractor lighting packages'],
    categoryId: 'cat-lighting',
    description:
      "150 W LED light fixture for warehouses and other high-ceiling spaces, safety-certified by UL and energy-listed by DLC. Sold under Pillar's own Pillar Pro brand to contractors and facility maintenance accounts. The subject of RFP-2026-014.",
  },
  'MAT-VFD': {
    id: 'MAT-VFD',
    name: 'Motor drive, 50 HP, 480 V',
    unit: 'each',
    annualVolume: 3_400,
    annualVolumeLabel: '3,400 drives',
    sites: ['HAY', 'PNL'],
    usedIn: ['Motor drive retrofit kits', 'Motor control panels', 'Resale'],
    categoryId: 'cat-drives',
    description:
      'UL-listed 50 HP, 480 V drive that controls motor speed, used in the control panel shop retrofit kits and motor control panels, and resold to industrial accounts. Currently single-sourced.',
  },
  'MAT-MCCB': {
    id: 'MAT-MCCB',
    name: 'Circuit breaker, 800 A',
    unit: 'each',
    annualVolume: 2_900,
    annualVolumeLabel: '2,900 breakers',
    sites: ['PNL', 'DAL'],
    usedIn: ['Motor control panels', 'Power distribution panels', 'Resale'],
    categoryId: 'cat-circuit',
    description:
      '3-pole, 480 V, UL-listed circuit breaker rated 800 A, built into control panel shop assemblies and resold. Primary source Vitrex, with Hollis approved as the backup.',
  },
  'MAT-PVC': {
    id: 'MAT-PVC',
    name: 'PVC electrical conduit, 1 in x 10 ft',
    unit: 'stick',
    annualVolume: 2_394_000,
    annualVolumeLabel: '2.39M lengths',
    sites: ['DAL'],
    usedIn: ['Contractor resale', 'Branch will-call'],
    categoryId: 'cat-conduit',
    description:
      'UL-listed PVC conduit (the plastic pipe that protects electrical wiring), 1 in wide in 10 ft lengths, sold to electrical contractors for underground and exposed runs. Single-sourced from Meridian.',
  },
};

export function isMaterialId(value: string): value is MaterialId {
  return Object.keys(MATERIALS).includes(value);
}

export function getMaterial(id: MaterialId): Material {
  return MATERIALS[id];
}

export const MATERIAL_LIST: Material[] = Object.values(MATERIALS);

/**
 * Per-supplier numbers for each material (trailing 12 months). Prices are USD
 * per material unit. For MAT-THHN the numbers are the canonical README values.
 */
export const MATERIAL_SUPPLIERS: SupplierMaterialMetrics[] = [
  // MAT-THHN: 1/0 AWG THHN copper building wire (price per C, 100 ft)
  {
    materialId: 'MAT-THHN',
    supplierId: 'sup-corvane',
    status: 'active',
    share: 52,
    unitPrice: 424.36,
    landedPrice: 424.36,
    incoterm: 'Delivered, freight included',
    leadTimeWeeks: 8,
    otif: 91.0,
    ppm: 820,
    ncrsTtm: 4,
    spendTtm: 31_300_000,
    contractId: 'SA-CORV-2024-017',
    tco: 446.1,
  },
  {
    materialId: 'MAT-THHN',
    supplierId: 'sup-hollis',
    status: 'active',
    share: 33,
    unitPrice: 437.0,
    landedPrice: 437.0,
    incoterm: 'Delivered, freight included',
    leadTimeWeeks: 5,
    otif: 98.1,
    ppm: 140,
    ncrsTtm: 0,
    spendTtm: 20_500_000,
    contractId: 'SA-HOLL-2024-021',
    tco: 440.2,
  },
  {
    materialId: 'MAT-THHN',
    supplierId: 'sup-kaisei',
    status: 'active',
    share: 15,
    unitPrice: 448.0,
    landedPrice: 462.3,
    incoterm: 'Pillar collects in Osaka',
    leadTimeWeeks: 12,
    otif: 94.0,
    ppm: 210,
    ncrsTtm: 1,
    spendTtm: 9_800_000,
    contractId: 'SA-KAIS-2025-003',
    tco: 468.9,
  },
  // MAT-LED: Pillar Pro LED high-bay fixture (price per fixture)
  {
    materialId: 'MAT-LED',
    supplierId: 'sup-aurelia',
    status: 'active',
    share: 68,
    unitPrice: 88.7,
    landedPrice: 88.7,
    incoterm: 'Delivered, freight included',
    leadTimeWeeks: 6,
    otif: 97.0,
    ppm: 95,
    ncrsTtm: 0,
    spendTtm: 7_840_000,
    contractId: 'SA-AURE-2024-009',
    tco: null,
  },
  {
    materialId: 'MAT-LED',
    supplierId: 'sup-northgate',
    status: 'active',
    share: 32,
    unitPrice: 89.95,
    landedPrice: 89.95,
    incoterm: 'Delivered, freight included',
    leadTimeWeeks: 4,
    otif: 93.0,
    ppm: 160,
    ncrsTtm: 1,
    spendTtm: 3_740_000,
    contractId: 'SA-NGTE-2024-012',
    tco: null,
  },
  // MAT-VFD: 50 HP, 480 V variable frequency drive (price per drive)
  {
    materialId: 'MAT-VFD',
    supplierId: 'sup-lumen',
    status: 'active',
    share: 100,
    unitPrice: 5_500,
    landedPrice: 5_500,
    incoterm: 'Delivered to Hayward DC, duties paid',
    leadTimeWeeks: 12,
    otif: 92.0,
    ppm: 180,
    ncrsTtm: 1,
    spendTtm: 18_700_000,
    contractId: 'SA-LUMN-2025-002',
    tco: null,
  },
  {
    materialId: 'MAT-VFD',
    supplierId: 'sup-kestrel',
    status: 'qualifying',
    share: 0,
    unitPrice: 6_050,
    landedPrice: 6_050,
    incoterm: 'Delivered to Hayward DC, duties paid',
    leadTimeWeeks: 8,
    otif: null,
    ppm: null,
    ncrsTtm: 0,
    spendTtm: 0,
    contractId: null,
    tco: null,
  },
  // MAT-MCCB: 800 A molded-case circuit breaker (price per breaker)
  {
    materialId: 'MAT-MCCB',
    supplierId: 'sup-vitrex',
    status: 'active',
    share: 80,
    unitPrice: 3_850,
    landedPrice: 3_850,
    incoterm: 'Delivered to Dallas panel shop, freight included',
    leadTimeWeeks: 10,
    otif: 96.2,
    ppm: 1450,
    ncrsTtm: 2,
    spendTtm: 8_930_000,
    contractId: 'SA-VITX-2023-004',
    tco: null,
  },
  {
    materialId: 'MAT-MCCB',
    supplierId: 'sup-hollis',
    status: 'active',
    share: 20,
    unitPrice: 4_020,
    landedPrice: 4_020,
    incoterm: 'Delivered to Dallas panel shop, freight included',
    leadTimeWeeks: 5,
    otif: 97.5,
    ppm: 160,
    ncrsTtm: 0,
    spendTtm: 2_330_000,
    contractId: 'SA-HOLL-2024-021',
    tco: null,
  },
  // MAT-PVC: Schedule 40 PVC conduit (price per 10 ft stick)
  {
    materialId: 'MAT-PVC',
    supplierId: 'sup-meridian',
    status: 'active',
    share: 100,
    unitPrice: 4.65,
    landedPrice: 4.65,
    incoterm: 'Delivered to Dallas DC, freight included',
    leadTimeWeeks: 3,
    otif: 95.5,
    ppm: 120,
    ncrsTtm: 0,
    spendTtm: 11_130_000,
    contractId: 'SA-MERI-2025-001',
    tco: null,
  },
];

/** Suppliers of one material, largest share first. */
export function materialSuppliers(
  materialId: MaterialId,
): SupplierMaterialMetrics[] {
  return MATERIAL_SUPPLIERS.filter((m) => m.materialId === materialId).sort(
    (a, b) => b.share - a.share,
  );
}

/** Metrics for one supplier of one material, or undefined when it does not supply it. */
export function materialSupplier(
  materialId: MaterialId,
  supplierId: SupplierId,
): SupplierMaterialMetrics | undefined {
  return MATERIAL_SUPPLIERS.find(
    (m) => m.materialId === materialId && m.supplierId === supplierId,
  );
}

/** Materials a supplier provides metrics for. */
export function materialsForSupplier(
  supplierId: SupplierId,
): SupplierMaterialMetrics[] {
  return MATERIAL_SUPPLIERS.filter((m) => m.supplierId === supplierId);
}

/**
 * MAT-THHN total cost of ownership per C (100 ft). Cost of poor quality and
 * expedites are spread over each supplier's volume: Corvane's 4 NCRs (jacket
 * defects, short reels, mislabeled lots) cost $1.1M in returns, re-spooling and
 * customer credits plus 31 hours of cut-to-length line downtime (about
 * $0.32M), and its 2 expedites cost $186K.
 */
export const TCO_BREAKDOWN: TcoBreakdown[] = [
  {
    supplierId: 'sup-corvane',
    unitPrice: 424.36,
    freightAndDuty: 0,
    costOfPoorQuality: 19.22,
    expedites: 2.52,
    tco: 446.1,
  },
  {
    supplierId: 'sup-hollis',
    unitPrice: 437.0,
    freightAndDuty: 0,
    costOfPoorQuality: 2.6,
    expedites: 0.6,
    tco: 440.2,
  },
  {
    supplierId: 'sup-kaisei',
    unitPrice: 448.0,
    freightAndDuty: 14.3,
    costOfPoorQuality: 3.1,
    expedites: 3.5,
    tco: 468.9,
  },
];

/**
 * Dollar detail behind Corvane's cost of poor quality and expedites (TTM).
 * `scrapAndRework` is the $1.1M of returns, re-spooling and customer credits.
 */
export const CORVANE_QUALITY_COSTS = {
  ncrs: 4,
  scrapAndRework: 1_100_000,
  /** Hours of cut-to-length line downtime. */
  downtimeHours: 31,
  expedites: 2,
  expediteCost: 186_000,
};

export interface ShareScenario {
  materialId: MaterialId;
  /** Contract renewal the shift is tied to. */
  decisionDate: string;
  fromSupplierId: SupplierId;
  toSupplierId: SupplierId;
  /** Percentage points moved. */
  pointsShifted: number;
  currentShares: Partial<Record<SupplierId, number>>;
  proposedShares: Partial<Record<SupplierId, number>>;
  /** Projected annual TCO savings, USD. */
  annualSavings: number;
  /** Components of `annualSavings`; they sum to it. */
  bridge: { label: string; amount: number }[];
}

/**
 * The AI recommendation on MAT-THHN: at the 2026-12-31 renewal of
 * SA-CORV-2024-017, move 15 points of share from Corvane to Hollis. The
 * per-C TCO gap on the shifted volume is only about $0.13M, so most of the
 * $0.9M comes from renewal leverage once Hollis is the larger supplier.
 */
export const THHN_SHARE_SCENARIO: ShareScenario = {
  materialId: 'MAT-THHN',
  decisionDate: '2026-12-31',
  fromSupplierId: 'sup-corvane',
  toSupplierId: 'sup-hollis',
  pointsShifted: 15,
  currentShares: { 'sup-corvane': 52, 'sup-hollis': 33, 'sup-kaisei': 15 },
  proposedShares: { 'sup-corvane': 37, 'sup-hollis': 48, 'sup-kaisei': 15 },
  annualSavings: 900_000,
  bridge: [
    {
      label:
        'Lower total cost on shifted volume (2.13M ft at $5.90 per 100 ft)',
      amount: 125_700,
    },
    {
      label:
        'Hold Corvane at the 2026 price for 2027 (avoids the +3% step tied to the copper market price)',
      amount: 668_800,
    },
    {
      label:
        'Smaller stock buffer and fewer rush orders from a 5-week lead time',
      amount: 105_500,
    },
  ],
};

// ---------------------------------------------------------------------------
// 24-month history. Deterministic: a small seeded PRNG drives the noise, and the
// last 12 months are fitted so their averages (OTIF, PPM) and total volume match
// the canonical trailing-12-month figures exactly.
// ---------------------------------------------------------------------------

interface MonthEvent {
  otif: number;
  ppm: number;
  ncrs: number;
}

interface HistorySpec {
  /** Contract price in force at month index i (0 = 24 months ago, 23 = this month). */
  contractPrice: (i: number) => number;
  /** Price paid at month index i; defaults to the contract price. */
  pricePaid?: (i: number, contractPrice: number, noise: number) => number;
  /** When set, the last-12-month mean of price paid is fitted to this value. */
  priceTtmMean?: number;
  otifBase: number;
  otifSpread: number;
  otifTtm: number;
  ppmBase: number;
  ppmSpread: number;
  ppmTtm: number;
  /** Quality and delivery events by month index. */
  events: Record<number, MonthEvent>;
  /** Units received in the last 12 months. */
  ttmVolume: number;
  /** Prior-year volume relative to the last 12 months. */
  priorYearVolumeFactor: number;
}

// Receipts follow the construction season, peaking April to September.
const VOLUME_SEASONALITY = [
  0.93, 0.94, 1.0, 1.04, 1.06, 1.07, 1.04, 1.05, 1.03, 1.01, 0.96, 0.87,
];

function hashString(s: string): number {
  let h = 2166136261;
  for (let i = 0; i < s.length; i += 1) {
    h ^= s.charCodeAt(i);
    h = Math.imul(h, 16777619);
  }
  return h >>> 0;
}

function mulberry32(seed: number): () => number {
  let a = seed >>> 0;
  return () => {
    a = (a + 0x6d2b79f5) >>> 0;
    let t = a;
    t = Math.imul(t ^ (t >>> 15), t | 1);
    t ^= t + Math.imul(t ^ (t >>> 7), t | 61);
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

/**
 * Adjust the free (non-fixed) values of the last 12 months by whole units of
 * 10^-decimals so that the last-12 sum equals `targetSum` exactly.
 */
function fitLastTwelve(
  values: number[],
  fixed: Set<number>,
  targetSum: number,
  decimals: number,
): number[] {
  const scale = 10 ** decimals;
  const scaled = values.map((v) => Math.round(v * scale));
  const free: number[] = [];
  for (let i = 12; i < 24; i += 1) {
    if (!fixed.has(i)) free.push(i);
  }
  const currentSum = scaled.slice(12).reduce((sum, v) => sum + v, 0);
  let residual = Math.round(targetSum * scale) - currentSum;
  const even = Math.trunc(residual / free.length);
  for (const i of free) scaled[i] += even;
  residual -= even * free.length;
  const step = residual > 0 ? 1 : -1;
  for (let k = 0; k < Math.abs(residual); k += 1) {
    scaled[free[k % free.length]] += step;
  }
  return scaled.map((v) => v / scale);
}

function buildHistory(key: string, spec: HistorySpec): MonthlyPoint[] {
  const months = monthKeysBack(24);
  const currentMonth = startOfToday().getMonth();
  const rng = mulberry32(hashString(key));
  const eventIdx = new Set(Object.keys(spec.events).map(Number));
  const symmetric = () => rng() * 2 - 1;

  const contractPrices = months.map((_, i) => spec.contractPrice(i));
  const paidRaw = months.map((_, i) => {
    const noise = symmetric();
    const contract = contractPrices[i];
    return spec.pricePaid ? spec.pricePaid(i, contract, noise) : contract;
  });
  const priceMean = spec.priceTtmMean;
  const paid =
    priceMean === undefined
      ? paidRaw
      : fitLastTwelve(paidRaw, new Set(), priceMean * 12, 2);

  const otifRaw = months.map((_, i) => {
    const event = spec.events[i];
    const noise = symmetric();
    return event ? event.otif : spec.otifBase + spec.otifSpread * noise;
  });
  const otif = fitLastTwelve(otifRaw, eventIdx, spec.otifTtm * 12, 1);

  const ppmRaw = months.map((_, i) => {
    const event = spec.events[i];
    const noise = symmetric();
    return event
      ? event.ppm
      : Math.max(10, spec.ppmBase + spec.ppmSpread * noise);
  });
  const ppm = fitLastTwelve(ppmRaw, eventIdx, spec.ppmTtm * 12, 0);

  const weightSum = VOLUME_SEASONALITY.reduce((sum, w) => sum + w, 0);
  const volumeRaw = months.map((_, i) => {
    const calendarMonth = (((currentMonth - (23 - i)) % 12) + 12) % 12;
    const weight = (VOLUME_SEASONALITY[calendarMonth] * 12) / weightSum;
    const base =
      (spec.ttmVolume / 12) * (i < 12 ? spec.priorYearVolumeFactor : 1);
    return base * weight * (1 + 0.04 * symmetric());
  });
  const volume = fitLastTwelve(volumeRaw, new Set(), spec.ttmVolume, 0);

  return months.map((month, i) => ({
    month,
    pricePaid: paid[i],
    contractPrice: contractPrices[i],
    otif: otif[i],
    ppm: ppm[i],
    volume: volume[i],
    ncrs: spec.events[i]?.ncrs ?? 0,
  }));
}

// Month indexes: 23 = Oct 2026 (this month), 22 = Sep, 21 = Aug, 20 = Jul,
// 19 = Jun, 18 = May, 17 = Apr, 16 = Mar, 15 = Feb, 14 = Jan 2026, 13 = Dec 2025,
// 12 = Nov 2025. Indexes 0-11 are the prior twelve months.
const HISTORY_SPECS: Partial<Record<string, HistorySpec>> = {
  // Corvane: price steps from 412.00 to 424.36 in Jan 2026, then the last two
  // months were billed at 431.50 (above contract). Four NCRs in the last 12
  // months, each in a month where OTIF dips and PPM spikes.
  'MAT-THHN:sup-corvane': {
    contractPrice: (i) => (i >= 14 ? 424.36 : 412.0),
    pricePaid: (i, contract) => (i >= 22 ? 431.5 : contract),
    otifBase: 94.5,
    otifSpread: 1.4,
    otifTtm: 91.0,
    ppmBase: 336,
    ppmSpread: 70,
    ppmTtm: 820,
    events: {
      5: { otif: 90.5, ppm: 880, ncrs: 1 },
      13: { otif: 84.5, ppm: 1650, ncrs: 1 },
      16: { otif: 82.0, ppm: 1900, ncrs: 1 },
      19: { otif: 86.0, ppm: 1500, ncrs: 1 },
      21: { otif: 83.5, ppm: 2100, ncrs: 1 },
    },
    ttmVolume: 73_840,
    priorYearVolumeFactor: 1.03,
  },
  // Hollis: flat price and reliable delivery.
  'MAT-THHN:sup-hollis': {
    contractPrice: () => 437.0,
    otifBase: 98.1,
    otifSpread: 0.7,
    otifTtm: 98.1,
    ppmBase: 140,
    ppmSpread: 25,
    ppmTtm: 140,
    events: {},
    ttmVolume: 46_860,
    priorYearVolumeFactor: 0.96,
  },
  // Kaisei: fixed $448.00/C FCA Osaka for the term (SA-KAIS-2025-003 §2.1); one NCR.
  'MAT-THHN:sup-kaisei': {
    contractPrice: () => 448.0,
    priceTtmMean: 448.0,
    otifBase: 94.4,
    otifSpread: 1.8,
    otifTtm: 94.0,
    ppmBase: 147,
    ppmSpread: 30,
    ppmTtm: 210,
    events: { 17: { otif: 89.0, ppm: 900, ncrs: 1 } },
    ttmVolume: 21_300,
    priorYearVolumeFactor: 0.98,
  },
  'MAT-LED:sup-aurelia': {
    contractPrice: () => 88.7,
    otifBase: 97.0,
    otifSpread: 1.0,
    otifTtm: 97.0,
    ppmBase: 95,
    ppmSpread: 20,
    ppmTtm: 95,
    events: {},
    ttmVolume: 88_400,
    priorYearVolumeFactor: 0.97,
  },
  'MAT-LED:sup-northgate': {
    contractPrice: () => 89.95,
    otifBase: 93.4,
    otifSpread: 1.8,
    otifTtm: 93.0,
    ppmBase: 131,
    ppmSpread: 35,
    ppmTtm: 160,
    events: { 15: { otif: 89.0, ppm: 480, ncrs: 1 } },
    ttmVolume: 41_600,
    priorYearVolumeFactor: 1.04,
  },
  'MAT-VFD:sup-lumen': {
    contractPrice: () => 5_500,
    otifBase: 92.5,
    otifSpread: 2.6,
    otifTtm: 92.0,
    ppmBase: 149,
    ppmSpread: 35,
    ppmTtm: 180,
    events: { 18: { otif: 86.0, ppm: 520, ncrs: 1 } },
    ttmVolume: 3_400,
    priorYearVolumeFactor: 0.95,
  },
  // Vitrex: PPM spikes in the last two months after the lot VX-24117 escape
  // (breakers from a factory that is not in the UL file).
  'MAT-MCCB:sup-vitrex': {
    contractPrice: () => 3_850,
    otifBase: 96.8,
    otifSpread: 0.9,
    otifTtm: 96.2,
    ppmBase: 380,
    ppmSpread: 70,
    ppmTtm: 1450,
    events: {
      22: { otif: 94.0, ppm: 5200, ncrs: 1 },
      23: { otif: 92.5, ppm: 8400, ncrs: 1 },
    },
    ttmVolume: 2_320,
    priorYearVolumeFactor: 0.97,
  },
  'MAT-MCCB:sup-hollis': {
    contractPrice: () => 4_020,
    otifBase: 97.5,
    otifSpread: 1.0,
    otifTtm: 97.5,
    ppmBase: 160,
    ppmSpread: 30,
    ppmTtm: 160,
    events: {},
    ttmVolume: 580,
    priorYearVolumeFactor: 0.9,
  },
  'MAT-PVC:sup-meridian': {
    contractPrice: () => 4.65,
    pricePaid: (_i, contract, noise) => contract * (1 + 0.015 * noise),
    priceTtmMean: 4.65,
    otifBase: 95.5,
    otifSpread: 1.5,
    otifTtm: 95.5,
    ppmBase: 120,
    ppmSpread: 25,
    ppmTtm: 120,
    events: {},
    ttmVolume: 2_394_000,
    priorYearVolumeFactor: 0.98,
  },
};

const HISTORY_CACHE = new Map<string, MonthlyPoint[]>();

/**
 * 24 months of price paid, OTIF, PPM and volume for one supplier of one
 * material, oldest first. Empty when the supplier has no history on the
 * material (for example Kestrel, which is still qualifying).
 */
export function materialHistory(
  materialId: MaterialId,
  supplierId: SupplierId,
): MonthlyPoint[] {
  const key = `${materialId}:${supplierId}`;
  const cached = HISTORY_CACHE.get(key);
  if (cached) return cached;
  const spec = HISTORY_SPECS[key];
  if (!spec) return [];
  const built = buildHistory(key, spec);
  HISTORY_CACHE.set(key, built);
  return built;
}
